import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Euro, History, Tags } from "lucide-react";

interface Tarifa {
  id: string;
  tecnico_id: string;
  empresa_id: string;
  coste_hora: number;
  coste_hora_extra: number | null;
  importe_furgoneta: number | null;
  categoria_id: string | null;
  vigente_desde: string;
  vigente_hasta: string | null;
  created_at: string;
  categoria?: { nombre: string } | null;
}

interface Categoria {
  id: string;
  nombre: string;
  coste_hora: number;
  coste_hora_extra: number;
  importe_furgoneta: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tecnicoId: string;
  tecnicoNombre: string;
}

const fmtEur = (v: number | null | undefined) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

export const TarifaTecnicoDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  tecnicoId,
  tecnicoNombre,
}) => {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [historico, setHistorico] = useState<Tarifa[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoriaSel, setCategoriaSel] = useState<string>("manual");
  const [costeHora, setCosteHora] = useState("");
  const [costeHoraExtra, setCosteHoraExtra] = useState("");
  const [furgoneta, setFurgoneta] = useState("0");
  const [vigenteDesde, setVigenteDesde] = useState(
    new Date().toISOString().split("T")[0]
  );

  const tarifaActual = historico.find((t) => t.vigente_hasta === null) ?? null;

  const fetchHistorico = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("tecnico_tarifas" as any)
      .select("*, categoria:tarifas_categoria(nombre)")
      .eq("tecnico_id", tecnicoId)
      .order("vigente_desde", { ascending: false });
    if (error) {
      toast({ title: "Error", description: "No se pudo cargar el histórico", variant: "destructive" });
    } else {
      setHistorico((data || []) as unknown as Tarifa[]);
    }
    setLoading(false);
  };

  const fetchCategorias = async () => {
    if (!perfil?.empresa_id) return;
    const { data } = await supabase
      .from("tarifas_categoria" as any)
      .select("id, nombre, coste_hora, coste_hora_extra, importe_furgoneta")
      .eq("empresa_id", perfil.empresa_id)
      .eq("activo", true)
      .order("nombre");
    setCategorias((data || []) as unknown as Categoria[]);
  };

  useEffect(() => {
    if (open) {
      fetchHistorico();
      fetchCategorias();
      setCategoriaSel("manual");
      setCosteHora("");
      setCosteHoraExtra("");
      setFurgoneta("0");
      setVigenteDesde(new Date().toISOString().split("T")[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tecnicoId]);

  const aplicarCategoria = (id: string) => {
    setCategoriaSel(id);
    if (id === "manual") return;
    const cat = categorias.find((c) => c.id === id);
    if (cat) {
      setCosteHora(String(cat.coste_hora));
      setCosteHoraExtra(String(cat.coste_hora_extra));
      setFurgoneta(String(cat.importe_furgoneta ?? 0));
    }
  };

  // ¿Los valores actuales coinciden con la categoría seleccionada?
  const categoriaSelObj = categorias.find((c) => c.id === categoriaSel);
  const valoresCoinciden =
    categoriaSelObj &&
    parseFloat(costeHora) === Number(categoriaSelObj.coste_hora) &&
    parseFloat(costeHoraExtra) === Number(categoriaSelObj.coste_hora_extra) &&
    parseFloat(furgoneta || "0") === Number(categoriaSelObj.importe_furgoneta ?? 0);

  const handleGuardar = async () => {
    const ch = parseFloat(costeHora);
    const che = parseFloat(costeHoraExtra);
    const furg = parseFloat(furgoneta || "0");
    if (isNaN(ch) || ch < 0) {
      toast({ title: "Coste inválido", variant: "destructive" });
      return;
    }
    if (costeHoraExtra.trim() === "" || isNaN(che) || che < 0) {
      toast({ title: "Coste hora extra obligatorio", variant: "destructive" });
      return;
    }
    if (isNaN(furg) || furg < 0) {
      toast({ title: "Furgoneta inválida", variant: "destructive" });
      return;
    }
    if (!perfil?.empresa_id) return;

    // Vincular a categoría solo si los valores coinciden exactamente
    const categoria_id = valoresCoinciden ? categoriaSel : null;

    setSaving(true);
    const { error } = await supabase.from("tecnico_tarifas" as any).insert({
      tecnico_id: tecnicoId,
      empresa_id: perfil.empresa_id,
      coste_hora: ch,
      coste_hora_extra: che,
      importe_furgoneta: furg,
      categoria_id,
      vigente_desde: vigenteDesde,
    });
    setSaving(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: "Tarifa guardada",
      description: categoria_id
        ? "Aplicada categoría. Recibirá futuras subidas automáticamente."
        : "Tarifa manual. No se verá afectada por cambios del catálogo.",
    });
    setCategoriaSel("manual");
    setCosteHora("");
    setCosteHoraExtra("");
    setFurgoneta("0");
    fetchHistorico();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5" />
            Tarifa de {tecnicoNombre}
          </DialogTitle>
          <DialogDescription>
            Aplica una categoría del catálogo o introduce los importes manualmente.
          </DialogDescription>
        </DialogHeader>

        {/* Tarifa actual */}
        <Card className="bg-muted/40">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Tarifa vigente</div>
            {tarifaActual ? (
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <div className="text-2xl font-bold">
                  {fmtEur(tarifaActual.coste_hora)}
                  <span className="text-sm font-normal text-muted-foreground"> /h</span>
                </div>
                {tarifaActual.coste_hora_extra != null && (
                  <div className="text-sm">
                    Extra: <strong>{fmtEur(tarifaActual.coste_hora_extra)}/h</strong>
                  </div>
                )}
                {tarifaActual.importe_furgoneta != null && Number(tarifaActual.importe_furgoneta) > 0 && (
                  <div className="text-sm">
                    Furgoneta: <strong>{fmtEur(tarifaActual.importe_furgoneta)}</strong>
                  </div>
                )}
                {tarifaActual.categoria?.nombre ? (
                  <Badge variant="secondary" className="gap-1">
                    <Tags className="h-3 w-3" />
                    {tarifaActual.categoria.nombre}
                  </Badge>
                ) : (
                  <Badge variant="outline">Manual</Badge>
                )}
                <div className="text-xs text-muted-foreground">
                  Desde {new Date(tarifaActual.vigente_desde).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground italic">Sin tarifa configurada</div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
          <span>
            <strong>Cambiar la tarifa solo afecta a jornadas futuras.</strong> Las jornadas
            ya registradas conservan el coste con el que se imputaron.
          </span>
        </div>

        {/* Nueva tarifa */}
        <div className="space-y-3 border-t pt-4">
          <h4 className="font-semibold">Establecer nueva tarifa</h4>

          <div>
            <Label>Aplicar categoría del catálogo</Label>
            <Select value={categoriaSel} onValueChange={aplicarCategoria}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">— Manual / sin categoría —</SelectItem>
                {categorias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nombre} · {fmtEur(c.coste_hora)}/h · extra {fmtEur(c.coste_hora_extra)}
                    {Number(c.importe_furgoneta) > 0 && ` · furg ${fmtEur(c.importe_furgoneta)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoriaSel !== "manual" && !valoresCoinciden && (
              <p className="text-xs text-amber-600 mt-1">
                Has modificado un campo: se guardará como tarifa manual sin vínculo con la categoría.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Coste/h (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={costeHora}
                onChange={(e) => setCosteHora(e.target.value)}
                placeholder="25.00"
              />
            </div>
            <div>
              <Label>Hora extra (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={costeHoraExtra}
                onChange={(e) => setCosteHoraExtra(e.target.value)}
              />
            </div>
            <div>
              <Label>Furgoneta (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={furgoneta}
                onChange={(e) => setFurgoneta(e.target.value)}
              />
            </div>
            <div>
              <Label>Vigente desde</Label>
              <Input
                type="date"
                value={vigenteDesde}
                onChange={(e) => setVigenteDesde(e.target.value)}
              />
            </div>
          </div>
          <Button
            onClick={handleGuardar}
            disabled={saving || !costeHora || !costeHoraExtra}
            className="w-full"
          >
            {saving ? "Guardando…" : "Guardar nueva tarifa"}
          </Button>
        </div>

        {/* Histórico */}
        <div className="border-t pt-4">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <History className="h-4 w-4" />
            Histórico de tarifas
          </h4>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : historico.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No hay tarifas registradas todavía
            </p>
          ) : (
            <div className="space-y-2">
              {historico.map((t) => (
                <div
                  key={t.id}
                  className={`flex flex-wrap justify-between gap-2 rounded-md border p-3 text-sm ${
                    t.vigente_hasta === null
                      ? "bg-emerald-500/5 border-emerald-500/30"
                      : "bg-muted/30"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold">
                      {fmtEur(t.coste_hora)}/h
                      {t.coste_hora_extra != null && (
                        <span className="text-muted-foreground font-normal">
                          {" "}· extra {fmtEur(t.coste_hora_extra)}/h
                        </span>
                      )}
                      {t.importe_furgoneta != null && Number(t.importe_furgoneta) > 0 && (
                        <span className="text-muted-foreground font-normal">
                          {" "}· furg {fmtEur(t.importe_furgoneta)}
                        </span>
                      )}
                    </div>
                    {t.categoria?.nombre && (
                      <Badge variant="secondary" className="gap-1 text-[10px]">
                        <Tags className="h-3 w-3" />
                        {t.categoria.nombre}
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {new Date(t.vigente_desde).toLocaleDateString()} –{" "}
                    {t.vigente_hasta
                      ? new Date(t.vigente_hasta).toLocaleDateString()
                      : "vigente"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
